import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AppleCareModalData {
  id: string;
  title: string;
  description: string;
  image: {
    src: string;
    alt: string;
  };
  keyFeatures: {
    title: string;
    items: string[];
  };
  legal: {
    title: string;
    content: string;
  };
  pricing?: {
    price: string;
    points?: string;
  };
  category?: string; // For video mapping
}

// Extended AppleCare option for modal generation
export interface AppleCareOptionExtended {
  id: string;
  modelName: string;
  price: string;
  points?: string;
  features: string[];
  isAppleCare: boolean;
  title: string;
  // Optional fields for richer modal content
  description?: string;
  image?: {
    src: string;
    alt: string;
  };
  legalContent?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppleCareModalService {
  private isModalOpenSubject = new BehaviorSubject<boolean>(false);
  private modalDataSubject = new BehaviorSubject<AppleCareModalData | null>(null);

  isModalOpen$: Observable<boolean> = this.isModalOpenSubject.asObservable();
  modalData$: Observable<AppleCareModalData | null> = this.modalDataSubject.asObservable();

  get currentModalData(): AppleCareModalData | null {
    return this.modalDataSubject.value;
  }

  // Primary method - works with your existing product data
  openModal(product: any): void {
    const appleCareData = this.createAppleCareData(product);
    this.modalDataSubject.next(appleCareData);
    this.isModalOpenSubject.next(true);
  }

  // Alternative method - works with your existing AppleCareOption data
  openModalFromAppleCareOption(appleCareOption: any, baseProduct: any): void {
    const appleCareData = this.createAppleCareDataFromOption(appleCareOption, baseProduct);
    this.modalDataSubject.next(appleCareData);
    this.isModalOpenSubject.next(true);
  }

  closeModal(): void {
    this.isModalOpenSubject.next(false);
    setTimeout(() => {
      this.modalDataSubject.next(null);
    }, 300);
  }

  // Create modal data from your existing AppleCareOption
  private createAppleCareDataFromOption(appleCareOption: any, baseProduct: any): AppleCareModalData {
    const productName = this.getProductDisplayName(baseProduct);
    const category = this.detectProductCategory(baseProduct);
    
    return {
      id: appleCareOption.id,
      title: appleCareOption.title || `AppleCare+ for ${productName}`,
      description: this.getDescription(productName, category),
      image: {
        src: this.getProductImage(baseProduct),
        alt: `AppleCare+ for ${productName}`
      },
      keyFeatures: {
        title: 'What\'s Included',
        items: appleCareOption.features || this.getFeatures(category)
      },
      legal: {
        title: 'Terms and Conditions',
        content: this.getLegalContent(category)
      },
      pricing: {
        price: appleCareOption.price,
        points: appleCareOption.points
      },
      category: category
    };
  }

  private createAppleCareData(product: any): AppleCareModalData {
    // Extract and format product information for the modal
    const productName = this.getProductDisplayName(product);
    const category = this.detectProductCategory(product);
    const features = this.getFeatures(category);
    const description = this.getDescription(productName, category);
    const pricing = this.extractPricing(product);
    
    return {
      id: `applecare-${product.productId}`,
      title: `AppleCare+ for ${productName}`,
      description: description,
      image: {
        src: this.getProductImage(product),
        alt: `AppleCare+ for ${productName}`
      },
      keyFeatures: {
        title: 'What\'s Included',
        items: features
      },
      legal: {
        title: 'Terms and Conditions',
        content: this.getLegalContent(category)
      },
      pricing: pricing,
      // Store the category for video mapping
      category: category
    };
  }

  private getProductDisplayName(product: any): string {
    let name = product.name || 'Apple Product';
    
    // Add model info if available from product name or specs
    const modelInfo = this.extractModelInfo(product);
    if (modelInfo) {
      name += ` ${modelInfo}`;
    }
    
    return name;
  }

  private extractModelInfo(product: any): string {
    const productName = product.name || '';
    
    // Extract common model patterns
    const patterns = [
      /(\d+GB)/i,           // Storage
      /(\d+TB)/i,           // Storage  
      /(\d+\.\d+["|′])/i,   // Screen size
      /(M\d+)/i,            // Chip
      /(Pro|Air|Mini)/i,    // Product line
    ];

    const modelParts = [];
    
    for (const pattern of patterns) {
      const match = productName.match(pattern);
      if (match && !modelParts.includes(match[1])) {
        modelParts.push(match[1]);
      }
    }

    return modelParts.join(' ');
  }

  private detectProductCategory(product: any): string {
    const productName = (product.name || '').toLowerCase();
    const categoryName = (product.category?.name || '').toLowerCase();
    
    if (productName.includes('iphone') || categoryName.includes('iphone')) {
      return 'iphone';
    }
    
    if (productName.includes('ipad') || categoryName.includes('ipad')) {
      return 'ipad';
    }
    
    if (productName.includes('macbook') || productName.includes('mac') || 
        categoryName.includes('mac')) {
      return 'macbook';
    }
    
    if (productName.includes('watch') || categoryName.includes('watch')) {
      return 'watch';
    }

    return 'other';
  }

  private getProductImage(product: any): string {
    // Try to get the first product image
    if (product.images && product.images.length > 0) {
      const firstSourceSet = product.images[0];
      if (firstSourceSet && firstSourceSet.length > 0) {
        return firstSourceSet[0];
      }
    }
    
    // Fallback to AppleCare logo
    return 'https://maxsite.b-cdn.net/Chase/Apple/Logos/applecare+.svg';
  }

  private getDescription(productName: string, category: string): string {
    const descriptions = {
      iphone: `Get complete peace of mind for your ${productName}. AppleCare+ extends your warranty and adds unlimited incidents of accidental damage protection, including coverage for theft and loss where available.`,
      ipad: `Keep your ${productName} protected with comprehensive coverage. AppleCare+ provides unlimited accidental damage protection and expert technical support whenever you need it.`,
      macbook: `Professional protection for your ${productName}. AppleCare+ includes hardware coverage, priority technical support, and onsite service options to keep you productive.`,
      watch: `Your ${productName} is built for an active lifestyle. AppleCare+ provides complete protection including water damage coverage and Express Replacement Service.`,
      other: `Extend your warranty and get expert support for your ${productName}. AppleCare+ provides comprehensive coverage and priority access to Apple technical support.`
    };

    return descriptions[category] || descriptions.other;
  }

  private extractPricing(product: any): any {
    // Look for AppleCare pricing in various places
    const appleCarePrice = product.applecarePrice || product.appleCarePrice;
    const appleCarePoints = product.applecarePoints || product.appleCarePoints;
    
    if (appleCarePrice || appleCarePoints) {
      return {
        price: appleCarePrice,
        points: appleCarePoints
      };
    }
    
    return undefined;
  }

  private getLegalContent(category: string): string {
    const baseContent = 'AppleCare+ extends your coverage and includes unlimited incidents of accidental damage protection. Each incident is subject to applicable fees.';
    
    const categorySpecific = {
      iphone: ' Theft and loss coverage requires Find My to be enabled on your device.',
      ipad: ' Coverage includes your iPad and Apple-branded accessories that come in the original box.',
      macbook: ' Onsite service is available for desktop computers. Portable computers can be serviced at an Apple Store.',
      watch: ' Coverage includes your Apple Watch, band, and charging accessories that come in the original box.',
      other: ' Service options vary by product and location.'
    };

    const specific = categorySpecific[category] || categorySpecific.other;
    return baseContent + specific;
  }

  private getFeatures(productName: string): string[] {
    const name = productName.toLowerCase();
    
    if (name.includes('iphone')) {
      return [
        'Unlimited incidents of accidental damage protection',
        'Express Replacement Service',
        'Coverage for theft and loss (where available)',
        '24/7 priority access to Apple experts',
        'Battery service when capacity drops below 80%'
      ];
    }
    
    if (name.includes('ipad')) {
      return [
        'Unlimited incidents of accidental damage protection',
        'Express Replacement Service',
        'Battery service coverage',
        '24/7 priority access to Apple experts',
        'Mail-in and carry-in repair service'
      ];
    }
    
    if (name.includes('macbook') || name.includes('mac')) {
      return [
        'Hardware repairs and replacements',
        'Priority access to telephone technical support',
        'Onsite service for desktop computers',
        'Coverage for portable computers and accessories',
        'Software support and assistance'
      ];
    }
    
    if (name.includes('watch')) {
      return [
        'Unlimited incidents of accidental damage protection',
        'Express Replacement Service',
        'Water damage protection',
        '24/7 priority access to Apple experts',
        'Coverage for Apple Watch and included band'
      ];
    }
    
    // Default features
    return [
      'Hardware coverage and technical support',
      'Priority access to Apple experts',
      'Repair and replacement services',
      'Global coverage'
    ];
  }
  private detectCategory(modelName: string): string {
  const name = modelName.toLowerCase();
  
  if (name.includes('iphone')) return 'iphone';
  if (name.includes('ipad')) return 'ipad';
  if (name.includes('macbook') || name.includes('mac')) return 'macbook';
  if (name.includes('watch')) return 'watch';
  
  return 'other';
}
  async fetchAppleCareData(modelName: string): Promise<AppleCareModalData> {
  // Generate dynamic content based on model name instead of hardcoded data
  const category = this.detectCategory(modelName);
  const features = this.getFeatures(category);
  const description = this.getDescription(modelName, category);
  
  return {
    id: `applecare-${modelName.toLowerCase().replace(/\s+/g, '-')}`,
    title: `AppleCare+ for ${modelName}`,
    description: description,
    image: {
      src: 'https://maxsite.b-cdn.net/Chase/Apple/Logos/applecare+.svg',
      alt: `AppleCare+ for ${modelName}`
    },
    keyFeatures: {
      title: 'What\'s Included',
      items: features
    },
    legal: {
      title: 'Terms and Conditions',
      content: this.getLegalContent(category)
    }
  };
}
}